import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, TemplateResult, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './ghs-billboard-cta.css.lit';
import '../../base/outline-container/outline-container';
import { SlotController } from '../../controllers/slot-controller';
import { HorizontalAlignmentVariant } from '../types';
export type CardColorVariant =
  | 'green-primary'
  | 'blue-primary'
  | 'neutral-lighter';
export type BillboardVariant = 'offset' | 'full' | 'feature';

/**
 * The The Billboard CTA component
 *
 * @element ghs-billboard-cta
 */
@customElement('ghs-billboard-cta')
export class GhsBillboardCta extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  slots = new SlotController(this, true);

  /**
   * The the Billboard variant.
   * @param billboardVariant
   * @default 'offset'
   */
  @property({ type: String, reflect: true })
  billboardVariant: BillboardVariant = 'offset';

  /**
   * The card color style variant to use.
   * @param colorVariant
   * @default 'blue-primary'
   */
  @property({ type: String, reflect: true })
  colorVariant: CardColorVariant = 'blue-primary';

  /**
   * The card's alignment relative to the image.
   * @param alignment
   * @default 'right-align'
   */
  @property({ type: String, reflect: true })
  alignment: HorizontalAlignmentVariant = 'right-align';

  /**
   * Displays a border in the content section of billboard, applies to the "feature" variant only.
   * @param border
   * @default false
   */
  @property({ type: Boolean, reflect: true })
  border: Boolean = false;

  @state() hasImageSlot: boolean;
  @state() hasEyebrowSlot: boolean;
  @state() hasHeadlineSlot: boolean;
  @state() hasSubHeadlineSlot: boolean;
  @state() hasBodySlot: boolean;
  @state() hasCtaSlot: boolean;

  /**
   * Sanitizes the color variant to allow compatibilty between Bellin and GHS
   * @param color The color property value.
   * @param site The site variant
   * @returns color class
   */
  protected getColorVariant(color: string, site: string) {
    switch (color + site) {
      case 'green-primaryBH':
        return 'blue-primary';
      case 'neutral-lighterGHS':
        return 'green-primary';
      default:
        return color;
    }
  }

  /**
   *  Template for the content section of the Billboard CTA component.
   * @returns Renderable markup.
   */
  protected renderContentSection(): TemplateResult {
    return html` <div class="billboard-content-section">
      ${this.hasEyebrowSlot
        ? html`
            <div class="billboard-eyebrow">
              <slot name="billboard-eyebrow"></slot>
            </div>
          `
        : nothing}
      ${this.hasHeadlineSlot
        ? html`
            <div class="billboard-headline">
              <slot name="billboard-headline"></slot>
            </div>
          `
        : nothing}
      ${this.hasSubHeadlineSlot
        ? html`
            <div class="billboard-sub-headline">
              <slot name="billboard-sub-headline"></slot>
            </div>
          `
        : nothing}
      ${this.hasBodySlot
        ? html`
            <div class="billboard-body-text">
              <slot name="billboard-body-text"></slot>
            </div>
          `
        : nothing}
      ${this.hasCtaSlot
        ? html`
            <div class="billboard-cta">
              <slot name="billboard-cta"></slot>
            </div>
          `
        : nothing}
    </div>`;
  }

  firstUpdated(): void {
    super.firstUpdated();
    this.hasImageSlot = this.slots.test('billboard-hero-image');
    this.hasEyebrowSlot = this.slots.test('billboard-eyebrow');
    this.hasHeadlineSlot = this.slots.test('billboard-headline');
    this.hasSubHeadlineSlot = this.slots.test('billboard-sub-headline');
    this.hasBodySlot = this.slots.test('billboard-body-text');
    this.hasCtaSlot = this.slots.test('billboard-cta');
  }

  render(): TemplateResult {
    return html`
      <div
        class="billboard ${this.billboardVariant} ${this.getColorVariant(
          this.colorVariant,
          this.site
        )} ${this.alignment}"
      >
        <outline-container
          ?full-bleed=${this.billboardVariant == 'feature'}
          class="billboard-inner"
        >
          ${this.hasImageSlot
            ? html`
                <div class="billboard-hero-image-wrapper">
                  <div class="billboard-hero-image">
                    <slot name="billboard-hero-image"></slot>
                  </div>
                </div>
              `
            : nothing}
          ${this.billboardVariant == 'feature'
            ? html`<div class="billboard-content-wrapper">
                ${this.renderContentSection()}
              </div>`
            : this.renderContentSection()}
        </outline-container>
      </div>
    `;
  }
}
